// Report-driven quarantine: when enough different players say a question is wrong,
// it is retired (retiredOn, see bank.ts) instead of staying in front of everyone
// until somebody notices. Pure on purpose: scripts/quarantine.mjs reads the
// reports from the database and applies the result to the question files.
// Reasons that mean "this question should not be asked as it stands". "unclear"
// (a typo, awkward wording) is for a person to fix, not a reason to pull it.
export const RETIRING_REASONS = ['wrong_answer', 'multiple_correct', 'outdated', 'offensive'];
// One report row is one player (the table has one row per player per question).
// The player base is small, so two independent players are enough; the change
// still goes through a pull request a person merges.
export const QUARANTINE_MIN_PLAYERS = 2;
// Only recent reports count, so a question that was fixed long ago isn't judged on old ones.
export const QUARANTINE_WINDOW_DAYS = 90;
const dayOf = (ms) => new Date(ms).toISOString().slice(0, 10);
export function addDays(date, days) {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
/**
 * Decides which reported questions to retire.
 *
 * Retired from tomorrow, not today: games dated today keep the pool they already
 * started with (the Daily must not change mid-day), and the server and client both
 * get the new bank before any game dated tomorrow is played.
 */
export function planQuarantine(reports, questions, today) {
    const byId = new Map(questions.map(q => [q.id, q]));
    const windowStart = addDays(today, -QUARANTINE_WINDOW_DAYS);
    const grouped = new Map();
    const serious = new Map();
    for (const r of reports) {
        const q = byId.get(r.question_id);
        if (!q || q.retiredOn)
            continue; // gone, or already on its way out
        const day = dayOf(r.created_at);
        if (day < windowStart)
            continue;
        if (q.reviewedOn && day <= q.reviewedOn)
            continue; // a person already dealt with these
        const entry = grouped.get(q.id) ?? { id: q.id, reports: 0, reasons: {} };
        entry.reports += 1;
        entry.reasons[r.reason] = (entry.reasons[r.reason] ?? 0) + 1;
        grouped.set(q.id, entry);
        if (RETIRING_REASONS.includes(r.reason))
            serious.set(q.id, (serious.get(q.id) ?? 0) + 1);
    }
    const mostReported = (a, b) => b.reports - a.reports || a.id.localeCompare(b.id);
    const retire = [];
    const review = [];
    for (const entry of grouped.values()) {
        (((serious.get(entry.id) ?? 0) >= QUARANTINE_MIN_PLAYERS) ? retire : review).push(entry);
    }
    return { retire: retire.sort(mostReported), retiredOn: addDays(today, 1), review: review.sort(mostReported) };
}
